import React, { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  addService,
  getCategories,
  updatedService,
  generateCodes,
  checkCodes,
  addPrefix,
  getPrefix,
} from "../../services/services";
import { toast } from "react-toastify";
import { debounce } from "lodash";
import headerLayoutWrapper from "../../hoc/headerLayoutWrapper";
import { ChevronDown } from "lucide-react";
import AddModal from "../Modal/PrefixModal";

const gstRates = [0, 5, 12, 18, 28];

const AddService = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const editData = location.state?.editData || null;

  const [formData, setFormData] = useState({
    name: "",
    category: "",
    gstRate: 0,
    taxType: "exclusive",
    baseCost: 0,
    details: "",
    description: "",
    serviceCode: "",
    sacCode: "",
    prefix: "",
    manualCode: "",
    productCode: "",
  });

  const [categories, setCategories] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [codeError, setCodeError] = useState("");
  const dropdownRef = useRef(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [modalType, setModalType] = useState("");
  const [newModalValue, setNewModalValue] = useState("");
  const [prefixList, setPrefixList] = useState(() => {
    const stored = localStorage.getItem("prefixList");
    return stored ? JSON.parse(stored) : ["noPrefix"];
  });
  const [errors, setErrors] = useState({});
  const nameRef = useRef(null);

  // Load edit data
  useEffect(() => {
    if (editData) {
      const code = editData.serviceCode || "";

      let prefix = "";
      let manual = "";

      // Extract prefix + number
      const match = code.match(/^([A-Z]+)-?(\d*)$/i);

      if (match) {
        prefix = match[1] ? match[1].toUpperCase() : "";
        manual = match[2] || "";
      }
      setFormData((prev) => ({
        ...prev,
        name: editData.name || "",
        category: editData.category?._id || "",
        gstRate: editData.gstRate || 0,
        taxType: editData.taxType || "exclusive",
        baseCost: editData.baseCost || 0,
        details: editData.details || "",
        description: editData.description || "",
        sacCode: editData.sacCode || "",

        prefix: prefix || "",
        manualCode: manual.replace(/^0+/, ""),
        serviceCode:
          prefix && manual ? `${prefix}-${manual}` : prefix ? prefix : manual,
      }));
    }
  }, [editData]);

  // Load categories
  useEffect(() => {
    loadCategories();
  }, []);

  const loadCategories = async () => {
    const payload = {
      type: "service",
    };
    try {
      const res = await getCategories(payload);
      if (res.data.status) {
        setCategories(res.data.response);
      }
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    const fetchPrefixes = async () => {
      try {
        const { data } = await getPrefix("service");

        const list = (data.prefixes || []).map((p) => p.name);

        setPrefixList(["noPrefix", ...list]);

        localStorage.setItem(
          "prefixList",
          JSON.stringify(["noPrefix", ...list]),
        );
      } catch (err) {
        toast.error("Failed to load prefixes");
      }
    };

    fetchPrefixes();
  }, []);

  const handleAddPrefix = async () => {
    const prefixValue = (newModalValue?.name || "").trim().toUpperCase();
    const prefixType = newModalValue?.type || "service";

    if (!prefixValue) return toast.error("Prefix cannot be empty");

    try {
      await addPrefix({
        name: prefixValue,
        type: prefixType, // service/product/sale/purchase
      });

      setPrefixList((p) => [...p, prefixValue]);
      localStorage.setItem(
        "prefixList",
        JSON.stringify([...prefixList, prefixValue]),
      );

      setFormData((prev) => ({
        ...prev,
        prefix: prefixValue,
        serviceCode: prefixValue + "-",
      }));

      setNewModalValue("");
      setShowAddModal(false);

      toast.success("Prefix added successfully");
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to add prefix");
    }
  };

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Debounced code check
  const handleCodeCheck = useCallback(
    debounce(async (code) => {
      if (!code.trim()) {
        setCodeError("");
        return;
      }
      try {
        const { data } = await checkCodes({ type: "service", code });
        setCodeError(data.valid ? "" : data.message);
      } catch {
        setCodeError("Error checking code");
      }
    }, 600),
    [],
  );

  // Auto SAC mapping
  const getSACCode = (category) => {
    const mapping = {
      Cleaning: "9985",
      Maintenance: "9987",
      Repair: "9983",
      Consulting: "9984",
      Training: "9992",
      General: "9999",
    };
    return mapping[category] || "9999";
  };

  const handleAutoGenerateServiceCodes = async () => {
    if (formData.manualCode.trim()) return;

    try {
      const sendPrefix = formData.prefix === "noPrefix" ? "" : formData.prefix;
      const { data } = await generateCodes({
        type: "service",
        prefix: sendPrefix,
      });

      if (data?.status) {
        const generatedCode = data.code;
        const match = generatedCode.match(/-(\d+)$/);
        const numericPart = match ? match[1].replace(/^0+/, "") : "";
        setFormData((p) => ({
          ...p,
          serviceCode: generatedCode,
          manualCode: numericPart,
          sacCode: getSACCode(p.category),
        }));
        toast.success(`Generated: ${generatedCode}`);
      } else {
        toast.error(data?.message || "Failed");
      }
    } catch {
      toast.error("Failed to generate code");
    }
  };

  const validate = () => {
    const newErrors = {};

    if (!formData.name.trim()) newErrors.name = "Service name is required";
    if (codeError) {
      newErrors.serviceCode = codeError;
    }
    // if (!formData.category) newErrors.category = "Category is required";

    // if (!formData.baseCost || Number(formData.baseCost) <= 0) {
    //   newErrors.baseCost = "Base cost must be greater than 0";
    // }

    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) {
      const firstErrorKey = Object.keys(newErrors)[0];

      toast.error(newErrors[firstErrorKey], {
        autoClose: 2500,
      });
      const focusMap = {
        name: nameRef,
      };

      focusMap[firstErrorKey]?.current?.focus();
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) {
      // toast.error("Please fix the highlighted errors");
      return;
    }

    const payload = {
      name: formData.name.trim(),
      category: formData.category,
      gstRate: Number(formData.gstRate),
      taxType: formData.taxType,
      baseCost: Number(formData.baseCost),
      details: formData.details,
      description: formData.description,
      serviceCode: formData.serviceCode,
      sacCode: formData.sacCode,
    };

    try {
      if (editData) {
        await updatedService(editData._id, payload);
        toast.success("Service updated");
      } else {
        await addService(payload);
        toast.success("Service added");
      }
      navigate(-1);
    } catch {
      toast.error("Operation failed");
    }
  };
  const handleBulkUpload = () => {
    navigate("/services/bulkupload");
  };
  return (
    <div id="main" className="add-item-page py-4 mb-4">
      <div className=" d-flex justify-content-between align-items-center mb-3">
        <h4 className="mb-0 custom-title">
          {editData ? "Edit Service" : "Add New Service"}
        </h4>
        <button
          type="button"
          className="btn btn-primary d-flex align-items-center gap-2 mb-0"
          onClick={handleBulkUpload}
        >
          {/* Icon for Mobile */}
          <i className="bi bi-upload d-inline d-md-none"></i>

          {/* Text for Desktop */}
          <span className="d-none d-md-inline">Bulk Upload</span>
        </button>
      </div>
      <div className="row">
        <div className="card shadow-sm">
          <form id="serviceForm" onSubmit={handleSubmit}>
            <div className="card-body">
              {/* Service Name */}
              <div className="col-md-12 mb-4">
                <label className="form-label fw-semibold">
                  Service Name <span className="text-danger ms-1">*</span>
                </label>
                <input
                  ref={nameRef}
                  type="text"
                  className={`form-control ${errors.name ? "is-invalid" : ""}`}
                  name="name"
                  value={formData.name}
                  onChange={(e) => {
                    setFormData((p) => ({ ...p, name: e.target.value }));
                    if (errors.name) {
                      setErrors((prev) => ({ ...prev, name: "" }));
                    }
                  }}
                  placeholder="e.g. AC Repair"
                />
                {errors.name && (
                  <div className="invalid-feedback">{errors.name}</div>
                )}
              </div>
              {/* Service Code + Prefix */}
              <div className="row">
                <div className="col-md-6 mb-4">
                  <label className="form-label fw-semibold">Service Code</label>

                  <div className="d-flex align-items-center gap-2 mb-2">
                    <select
                      className="form-select w-auto"
                      value={formData.prefix || "noPrefix"}
                      onChange={(e) => {
                        const value = e.target.value;

                        if (value === "addNew") {
                          setModalType("prefix");
                          setNewModalValue({ name: "", type: "service" });
                          setShowAddModal(true);
                          return;
                        }

                        const prefix = value === "noPrefix" ? "" : value;
                        const fullCode =
                          prefix && formData.manualCode
                            ? `${prefix}-${formData.manualCode}`
                            : prefix
                              ? prefix
                              : formData.manualCode || "";

                        setFormData((p) => ({
                          ...p,
                          prefix: value === "noPrefix" ? "" : value,
                          serviceCode: fullCode,
                          productCode: fullCode,
                        }));
                      }}
                    >
                      <option value="noPrefix">No Prefix</option>
                      {prefixList
                        .filter((p) => p !== "noPrefix")
                        .map((p) => (
                          <option key={p} value={p}>
                            {p}
                          </option>
                        ))}
                      <option value="addNew" className="text-primary">
                        + Add New Prefix
                      </option>
                    </select>
                    <div className="input-group">
                      <input
                        type="text"
                        className={`form-control ${codeError ? "is-invalid" : ""
                          }`}
                        value={formData.manualCode}
                        onChange={(e) => {
                          const val = e.target.value
                            .toUpperCase()
                            .replace(/[^A-Z0-9-]/g, "");

                          const prefix = formData.prefix || "";
                          const finalCode = prefix ? `${prefix}-${val}` : val;

                          setFormData((p) => ({
                            ...p,
                            manualCode: val,
                            serviceCode: finalCode,
                          }));

                          handleCodeCheck(val);
                        }}
                      />
                      {/* <button
                        type="button"
                        className="btn btn-outline-primary btn-sm"
                        onClick={handleAutoGenerateServiceCodes}
                        title="Auto Generate"
                      >
                        <i className="bi bi-arrow-repeat"></i>
                      </button> */}
                      {codeError && (
                        <div className="invalid-feedback">{codeError}</div>
                      )}
                    </div>
                  </div>
                </div>

                {/* SAC Code */}
                <div className="col-md-6 mb-4">
                  <label className="form-label fw-semibold">SAC Code</label>
                  <input
                    type="text"
                    className="form-control"
                    value={formData.sacCode}
                    onChange={(e) =>
                      setFormData((p) => ({
                        ...p,
                        sacCode: e.target.value.replace(/[^a-zA-Z0-9-]/g, ""),
                      }))
                    }
                  />
                </div>
              </div>

              {/* Pricing */}
              <div className="row mb-4">
                <div className="col-md-6">
                  <label className="form-label fw-semibold">
                    Base Price (₹)
                  </label>
                  <div className="input-group">
                    <input
                      type="text"
                      inputMode="decimal"
                      className={`form-control ${errors.baseCost ? "is-invalid" : ""
                        }`}
                      value={formData.baseCost}
                      onChange={(e) => {
                        let value = e.target.value;

                        // Remove invalid characters (allow digits, dot, minus)
                        value = value.replace(/[^0-9.-]/g, "");

                        // Allow only one minus at start
                        if (value.includes("-")) {
                          value = value.replace(/-/g, "");
                          value = "-" + value;
                        }

                        // Allow only one decimal point
                        const parts = value.split(".");
                        if (parts.length > 2) {
                          value = parts[0] + "." + parts.slice(1).join("");
                        }

                        setFormData((p) => ({
                          ...p,
                          baseCost: value,
                        }));
                      }}
                      min="0"
                      step="0.01"
                    />

                    <button
                      type="button"
                      className="btn border d-flex align-items-center fw-semibold"
                      style={{ whiteSpace: "nowrap" }}
                      onClick={() =>
                        setFormData((p) => ({
                          ...p,
                          taxType:
                            p.taxType === "exclusive"
                              ? "inclusive"
                              : "exclusive",
                        }))
                      }
                    >
                      {formData.taxType === "exclusive" ? (
                        <>
                          Exclusive <ChevronDown size={16} className="ms-1" />
                        </>
                      ) : (
                        <>
                          Inclusive <ChevronDown size={16} className="ms-1" />
                        </>
                      )}
                    </button>

                    {/* Live Inclusive/Exclusive Calculation */}
                    {formData.baseCost && formData.gstRate ? (
                      <div
                        className="p-2 rounded-end border bg-light"
                        style={{ fontSize: "0.9rem" }}
                      >
                        <div className="d-flex justify-content-between">
                          <strong>
                            ₹
                            {formData.taxType === "exclusive"
                              ? (
                                formData.baseCost *
                                (1 + formData.gstRate / 100)
                              ).toFixed(2)
                              : (
                                formData.baseCost /
                                (1 + formData.gstRate / 100)
                              ).toFixed(2)}
                          </strong>
                        </div>
                      </div>
                    ) : null}
                  </div>
                </div>

                <div className="col-md-6">
                  <label className="form-label fw-semibold">GST Rate (%)</label>
                  <select
                    className="form-select"
                    value={formData.gstRate}
                    onChange={(e) =>
                      setFormData((p) => ({
                        ...p,
                        gstRate: Number(e.target.value),
                      }))
                    }
                  >
                    <option value="">Select GST</option>
                    {gstRates.map((r) => (
                      <option key={r} value={r}>
                        {r}%
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="row">
                {/* Category */}
                <div className="col-md-6 mb-4">
                  <label className="form-label">Category </label>
                  <select
                    className="form-select"
                    name="category"
                    value={formData.category}
                    onChange={(e) =>
                      setFormData((p) => ({ ...p, category: e.target.value }))
                    }
                  >
                    {categories.length == 0 ? (
                      <option value="">No categories</option>
                    ) : (
                      <>
                        <option value="" disabled>
                          Select category
                        </option>
                        {categories.map((cat) => (
                          <option key={cat?._id} value={cat?._id}>
                            {cat?.name}
                          </option>
                        ))}
                      </>
                    )}
                  </select>
                </div>
                <div className="col-md-6 mb-2">
                  <label className="form-label fw-semibold">Details</label>
                  <input
                    className="form-control"
                    value={formData.details}
                    onChange={(e) =>
                      setFormData((p) => ({ ...p, details: e.target.value }))
                    }
                  />
                </div>
              </div>
            </div>
            <div className="col-md-6 mb-4 ps-4">
              <label className="form-label fw-semibold">Description</label>
              <textarea
                className="form-control"
                rows="3"
                value={formData.description}
                onChange={(e) =>
                  setFormData((p) => ({
                    ...p,
                    description: e.target.value,
                  }))
                }
              />
            </div>
            <div className="d-flex justify-content-end gap-3 p-4 mb-5 mb-lg-0">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => navigate(-1)}
              >
                Cancel
              </button>
              <button type="submit" form="serviceForm" className="btn btn-primary">
                {editData ? "Update Service" : "Add Service"}
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* Modal for Adding Prefix */}
      <AddModal
        show={showAddModal && modalType === "prefix"}
        onClose={() => {
          setNewModalValue("");
          setShowAddModal(false);
        }}
        type="prefix"
        value={newModalValue}
        onChange={setNewModalValue}
        onSave={handleAddPrefix}
        title="Add Prefix"
        label="Prefix Name"
        placeholder="Enter service prefix "
        showTypeDropdown={false}
      />
    </div>
  );
};

export default headerLayoutWrapper(AddService);
